import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useTrackClickedPublicBoardLink } from "@air/analytics";
import Router from "next/router";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PublicTopBarAccountInfo } from "~/components/Layouts/PublicBoardLayout/PublicTopBarAccountInfo";
import { PublicBoardView } from "~/components/PublicBoard/PublicBoardView";
import { PublicBoardActivityContainer } from "~/components/PublicBoardActivityContainer";
import { PublicBoardAssetModal } from "~/components/PublicBoardAsset/PublicBoardAssetModal";
import { PUBLIC_TOP_BAR_HEIGHT, PublicTopBar } from "~/components/PublicTopBar";
import { PublicWorkspaceInfo } from "~/components/PublicWorkspaceInfo";
import { PublicWorkspaceNavigation } from "~/components/PublicWorkspaceNavigation";
import { usePublicWorkspace } from "~/components/PublicWorkspaceProvider";
import { MainContent } from "~/components/Shared/ClipGallery/Content";
import { PublicHeaderBar } from "~/components/Shared/HeaderBar/PublicHeaderBar";
import { SCROLL_CONTAINER_ID } from "~/components/TableView/ui";
import { createBoardRoute } from "~/constants/routes";
import { MAIN, PUBLIC_BOARD_CHILDREN_CONTAINER } from "~/constants/testIDs";
import { useRerenderOnMount } from "~/hooks/useRerenderOnMount";
import { useURLClipIdSelector } from "~/hooks/useURLClipIdSelector";
import { useAccountContext } from "~/providers/AccountProvider";
import { AnnotationProvider } from "~/providers/AnnotationProvider/AnnotationProvider";
import { centralizedBoardSelector } from "~/store/centralizedBoard/selectors";
import { useWorkspaces } from "~/swr-hooks/workspaces/useWorkspaces";
import { useAirStore } from "~/utils/ReduxUtils";
export const ViewableBoard = /*#__PURE__*/ memo(()=>{
    const store = useAirStore();
    const { data: account } = useAccountContext();
    const { data: workspaces } = useWorkspaces();
    const { workspaceId } = usePublicWorkspace();
    const mainRef = useRef(null);
    const headerRef = useRef(null);
    const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();
    const clipId = useURLClipIdSelector();
    const { isMounted } = useRerenderOnMount();
    const [isVisitBannerVisible, setVisitBannerVisible] = useState(()=>{
        if (!workspaces || !workspaceId) return false;
        return workspaces.some((workspace)=>workspace.id === workspaceId);
    });
    useEffect(()=>{
        if (isVisitBannerVisible || !workspaceId || !workspaces) return;
        setVisitBannerVisible(workspaces.some((workspace)=>workspace.id === workspaceId));
    }, [
        workspaceId,
        isVisitBannerVisible,
        workspaces
    ]);
    const onWorkspaceNavigate = useCallback(()=>{
        const centralizedBoard = centralizedBoardSelector(store.getState());
        if (!centralizedBoard) return;
        Router.push(createBoardRoute(centralizedBoard.id, centralizedBoard.title));
        trackClickedPublicBoardLink({
            name: "clicked view in workspace",
            page_area: "banner",
            page_type: "public board"
        });
    }, [
        store,
        trackClickedPublicBoardLink
    ]);
    const workspaceInfo = useMemo(()=>isVisitBannerVisible ? /*#__PURE__*/ _jsx(PublicWorkspaceNavigation, {
            onWorkspaceNavigate: onWorkspaceNavigate
        }) : /*#__PURE__*/ _jsx(PublicWorkspaceInfo, {}), [
        isVisitBannerVisible,
        onWorkspaceNavigate
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PublicTopBar, {
                workspaceInfo: workspaceInfo,
                accountInfo: account ? /*#__PURE__*/ _jsx(PublicTopBarAccountInfo, {
                    account: account
                }) : null
            }),
            /*#__PURE__*/ _jsxs(MainContent, {
                id: SCROLL_CONTAINER_ID,
                "data-testid": MAIN,
                ref: mainRef,
                style: {
                    height: "calc(100% - ".concat(PUBLIC_TOP_BAR_HEIGHT, "px)")
                },
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex h-fit flex-1 flex-col",
                        children: [
                            /*#__PURE__*/ _jsx(PublicHeaderBar, {
                                headerRef: headerRef
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grow",
                                "data-testid": PUBLIC_BOARD_CHILDREN_CONTAINER,
                                children: [
                                    /*#__PURE__*/ _jsx(PublicBoardView, {
                                        headerRef: headerRef,
                                        scrollElementRef: mainRef
                                    }),
                                    !!clipId && isMounted && /*#__PURE__*/ _jsx(AnnotationProvider, {
                                        children: /*#__PURE__*/ _jsx(PublicBoardAssetModal, {})
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(PublicBoardActivityContainer, {})
                ]
            })
        ]
    });
});
ViewableBoard.displayName = "ViewableBoard";
